/**
 * Workspace Modules Settings Page
 *
 * Allows workspace admins to turn major platform modules on or off, and to
 * configure the per-module override policy — who can set a personal feature-flag
 * override, and what the default state is for members when the module is enabled.
 *
 * Module settings are persisted in workspaces.features JSONB (toggle) and
 * workspaces.module_policies JSONB (policy) and apply to all workspace members.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ToggleLeft, AlertTriangle, ChevronDown, ChevronRight, Users, Lock, Ban, ExternalLink } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import {
  WORKSPACE_MODULES,
  getWorkspaceModules,
  setWorkspaceModule,
  setWorkspaceModulePolicy,
  isModuleEnabled,
  isModuleListedFor,
  isSwitchedOffModuleKey,
  getModulePolicy,
  type WorkspaceModule,
  type ModulePolicy,
} from '../../../lib/workspaceModulesService';
import {
  MODULE_PAGE_SECTIONS,
  MODULE_SETTINGS_GROUPS,
  groupsForSection,
  groupModules,
  getModulePageSection,
  type ModulePageSectionId,
  type ModuleSettingsGroupId,
} from '../../../lib/moduleGroups';
import { getModulePrimaryRoute } from '../../../lib/moduleRoutes';
import { useIsElevationTeam } from '../../../lib/elevationAccess';
import { FLAG_CONFIGS } from '../../../lib/featureFlags';
import { DelegationLadderSetting } from '../../../components/prioritization/commitments/DelegationParts';
import type { FeatureFlagValue } from '../../../lib/featureFlags';

// ============================================================================
// Override policy helpers
// ============================================================================

const OVERRIDE_LABELS: Record<ModulePolicy['override'], string> = {
  all: 'All members',
  admins_only: 'Admins only',
  none: 'No override',
};

const OVERRIDE_DESCRIPTIONS: Record<ModulePolicy['override'], string> = {
  all: 'Any workspace member can turn this feature on or off for themselves.',
  admins_only: 'Only workspace admins can toggle their personal flag. Other members always see the workspace default.',
  none: 'No individual override — the module toggle applies uniformly to all members.',
};

const OVERRIDE_ICONS: Record<ModulePolicy['override'], React.ReactNode> = {
  all: <Users className="h-3.5 w-3.5" />,
  admins_only: <Lock className="h-3.5 w-3.5" />,
  none: <Ban className="h-3.5 w-3.5" />,
};

// ============================================================================
// Individual Control section (shown inside each live module card)
// ============================================================================

interface IndividualControlProps {
  module: WorkspaceModule;
  policy: ModulePolicy;
  moduleEnabled: boolean;
  onPolicyChange: (moduleKey: string, policy: Partial<ModulePolicy>) => void;
  saving: boolean;
}

function IndividualControl({ module, policy, moduleEnabled, onPolicyChange, saving }: IndividualControlProps) {
  const [expanded, setExpanded] = useState(false);

  // Only render Individual Control when the paired flag is actually registered —
  // an unregistered flagKey means the gate cannot consult a personal override,
  // so advertising one would be a dead control (build contract §2.7).
  const flagConfig = module.flagKey
    ? FLAG_CONFIGS[module.flagKey as FeatureFlagValue]
    : undefined;

  if (!module.flagKey || !flagConfig) {
    return (
      <div className="mt-3 pt-3 border-t border-[var(--rule)] dark:border-[var(--rule)]">
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] italic">
          {module.flagKey
            ? `Individual flag "${module.flagKey}" is not registered — the module applies uniformly until the flag is added to FLAG_CONFIGS.`
            : 'No individual flag — this module applies uniformly to all workspace members.'}
        </p>
      </div>
    );
  }
  const effectiveUserDefault = policy.userDefault ?? module.userDefault ?? true;

  return (
    <div className="mt-3 pt-3 border-t border-[var(--rule)] dark:border-[var(--rule)]">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)] w-full text-left"
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 flex-shrink-0" />
        )}
        <span className="font-medium">Individual control</span>
        <span className="ml-auto flex items-center gap-1 text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          {OVERRIDE_ICONS[policy.override]}
          <span>{OVERRIDE_LABELS[policy.override]}</span>
          {policy.override !== 'none' && (
            <span className="text-[var(--text-ter)] dark:text-[var(--text-dim)]">·</span>
          )}
          {policy.override !== 'none' && (
            <span>Default: {effectiveUserDefault ? 'On' : 'Off'}</span>
          )}
        </span>
      </button>

      {expanded && (
        <div className="mt-3 bg-[var(--surface)] border border-[var(--rule)] p-3 space-y-4 dark:bg-[var(--bg-raised)] dark:border-[var(--rule)]">
          {/* Linked flag */}
          <div>
            <p className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">Linked individual flag</p>
            <div className="flex items-center gap-2">
              <code className="text-xs bg-[var(--bg-raised)] border border-[var(--rule)] px-1.5 py-0.5 rounded text-[var(--text-sec)] dark:bg-[var(--surface)] dark:border-[var(--rule)] dark:text-[var(--text-sec)]">
                {module.flagKey}
              </code>
              {flagConfig && (
                <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">{flagConfig.name}</span>
              )}
            </div>
            {flagConfig && (
              <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1">{flagConfig.description}</p>
            )}
          </div>

          {/* Who can override */}
          <div>
            <p className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-2">Who can override</p>
            <div className="space-y-1.5">
              {(['all', 'admins_only', 'none'] as const).map((val) => (
                <label
                  key={val}
                  className={`flex items-start gap-2 cursor-pointer  px-2 py-1.5 border transition-colors ${
                    policy.override === val
                      ? 'border-primary-300 bg-primary-50 dark:border-[#5A9B72]/50 dark:bg-[rgba(58,125,86,0.15)]'
                      : 'border-transparent bg-[var(--bg-raised)] hover:border-[var(--rule)] dark:bg-[var(--surface)] dark:hover:border-[var(--rule-bright)]'
                  }`}
                >
                  <input
                    type="radio"
                    name={`override-${module.key}`}
                    value={val}
                    checked={policy.override === val}
                    disabled={saving}
                    onChange={() =>
                      // Always On: No override forces the feature on for everyone.
                      // Persist userDefault: true in the same write — the Default
                      // control is hidden when override is 'none', so a stale
                      // userDefault: false would otherwise force everyone off.
                      onPolicyChange(
                        module.key,
                        val === 'none'
                          ? { override: val, userDefault: true }
                          : { override: val },
                      )
                    }
                    className="mt-0.5 h-3.5 w-3.5 text-primary-600"
                  />
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">{OVERRIDE_ICONS[val]}</span>
                      <span className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)]">{OVERRIDE_LABELS[val]}</span>
                    </div>
                    <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">{OVERRIDE_DESCRIPTIONS[val]}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Default for members (only shown when override is not 'none') */}
          {policy.override !== 'none' && (
            <div>
              <p className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">Default for members</p>
              <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-2">
                When the workspace enables this module, should members have it on or off by default?
              </p>
              <div className="flex items-center gap-3">
                <label className={`flex items-center gap-1.5 cursor-pointer  px-2 py-1.5 border text-xs transition-colors ${
                  effectiveUserDefault ? 'border-[var(--green)] bg-[var(--green-dim)] text-[var(--green)] dark:border-[var(--green)]/50 dark:bg-[var(--green)]/25 dark:text-[var(--text-ter)]' : 'border-transparent bg-[var(--bg-raised)] text-[var(--text-ter)] hover:border-[var(--rule)] dark:bg-[var(--surface)] dark:text-[var(--text-ter)] dark:hover:border-[var(--rule-bright)]'
                }`}>
                  <input
                    type="radio"
                    name={`userDefault-${module.key}`}
                    checked={effectiveUserDefault}
                    disabled={saving || !moduleEnabled}
                    onChange={() => onPolicyChange(module.key, { userDefault: true })}
                    className="h-3.5 w-3.5 text-[var(--green)] dark:text-[var(--text-ter)]"
                  />
                  <span className="font-medium">On</span>
                  <span className="text-[var(--text-ter)] dark:text-[var(--text-dim)] hidden sm:inline">— opt-out model</span>
                </label>
                <label className={`flex items-center gap-1.5 cursor-pointer  px-2 py-1.5 border text-xs transition-colors ${
                  !effectiveUserDefault ? 'border-[var(--mustard)] bg-[var(--mustard-dim)] text-[var(--mustard)] dark:border-[var(--mustard)]/50 dark:bg-[var(--mustard)]/25 dark:text-[var(--mustard)]' : 'border-transparent bg-[var(--bg-raised)] text-[var(--text-ter)] hover:border-[var(--rule)] dark:bg-[var(--surface)] dark:text-[var(--text-ter)] dark:hover:border-[var(--rule-bright)]'
                }`}>
                  <input
                    type="radio"
                    name={`userDefault-${module.key}`}
                    checked={!effectiveUserDefault}
                    disabled={saving || !moduleEnabled}
                    onChange={() => onPolicyChange(module.key, { userDefault: false })}
                    className="h-3.5 w-3.5 text-[var(--mustard)]"
                  />
                  <span className="font-medium">Off</span>
                  <span className="text-[var(--text-ter)] dark:text-[var(--text-dim)] hidden sm:inline">— opt-in model</span>
                </label>
              </div>
              {!moduleEnabled && (
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1 italic">Enable the module to configure member defaults.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Module group section (2-column grid)
// ============================================================================

interface ModuleGroupSectionProps {
  groupId: ModuleSettingsGroupId;
  modules: WorkspaceModule[];
  features: Record<string, boolean>;
  getPolicy: (moduleKey: string) => ModulePolicy;
  onToggle: (key: string, enabled: boolean) => void;
  onPolicyChange: (moduleKey: string, policy: Partial<ModulePolicy>) => void;
  saving: string | null;
  variant: 'full' | 'tool';
}

function ModuleGroupSection({
  groupId,
  modules,
  features,
  getPolicy,
  onToggle,
  onPolicyChange,
  saving,
  variant,
}: ModuleGroupSectionProps) {
  if (modules.length === 0) return null;

  const section = MODULE_SETTINGS_GROUPS[groupId];

  return (
    <div>
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-[var(--text-sec)] dark:text-[var(--text-pri)]">{section.title}</h3>
        <p className="mt-0.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">{section.description}</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {modules.map((mod) =>
          variant === 'tool' ? (
            <ToolModuleRow
              key={mod.key}
              module={mod}
              enabled={isModuleEnabled(features, mod.key)}
              onToggle={onToggle}
              saving={saving === mod.key}
            />
          ) : (
            <ModuleRow
              key={mod.key}
              module={mod}
              enabled={isModuleEnabled(features, mod.key)}
              blockedBy={mod.requires && !isModuleEnabled(features, mod.requires)
                ? (modules.find((m) => m.key === mod.requires)?.name ?? mod.requires)
                : null}
              policy={getPolicy(mod.key)}
              onToggle={onToggle}
              onPolicyChange={onPolicyChange}
              saving={saving === mod.key}
            />
          ),
        )}
      </div>
    </div>
  );
}

function ModulesPageSection({
  sectionId,
  modules,
  features,
  getPolicy,
  onToggle,
  onPolicyChange,
  saving,
}: {
  sectionId: ModulePageSectionId;
  modules: WorkspaceModule[];
  features: Record<string, boolean>;
  getPolicy: (moduleKey: string) => ModulePolicy;
  onToggle: (key: string, enabled: boolean) => void;
  onPolicyChange: (moduleKey: string, policy: Partial<ModulePolicy>) => void;
  saving: string | null;
}) {
  const pageSection = MODULE_PAGE_SECTIONS[sectionId];
  const grouped = groupModules(modules);
  const groupOrder = groupsForSection(sectionId);
  const variant = sectionId === 'tools' ? 'tool' : 'full';

  if (modules.length === 0) return null;

  return (
    <div>
      {sectionId !== 'tools' && (
        <div className="mb-4 flex items-start gap-2">
          <div>
            <h2 className="text-sm font-semibold text-[var(--text-sec)] dark:text-[var(--text-pri)]">{pageSection.title}</h2>
            <p className="mt-0.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">{pageSection.description}</p>
          </div>
        </div>
      )}
      <div className="space-y-8">
        {groupOrder.map((groupId) => (
          <ModuleGroupSection
            key={groupId}
            groupId={groupId}
            modules={grouped[groupId] ?? []}
            features={features}
            getPolicy={getPolicy}
            onToggle={onToggle}
            onPolicyChange={onPolicyChange}
            saving={saving}
            variant={variant}
          />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Module Row
// ============================================================================

interface ModuleRowProps {
  module: WorkspaceModule;
  enabled: boolean;
  /** The name of the module this one rides on, while that one is off (module.requires). */
  blockedBy?: string | null;
  policy: ModulePolicy;
  onToggle: (key: string, enabled: boolean) => void;
  onPolicyChange: (moduleKey: string, policy: Partial<ModulePolicy>) => void;
  saving: boolean;
}

function ModuleOpenLink({ moduleKey }: { moduleKey: string }) {
  const elevationTeam = useIsElevationTeam();
  const route = getModulePrimaryRoute(moduleKey);
  if (!route) return null;
  // The Team pages module opens the team hub at /home, which is only
  // for the team (lib/elevationAccess.ts); for anyone else this link would
  // land on the dashboard, so it is not drawn.
  if (moduleKey === 'elevation_platform' && !elevationTeam) return null;
  return (
    <Link
      to={route}
      className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-primary-600 hover:text-primary-700 hover:underline dark:text-[#5A9B72] dark:hover:text-[#6BB088]"
    >
      Open in app
      <ExternalLink className="h-3 w-3" />
    </Link>
  );
}

function ModuleRow({ module, enabled, blockedBy, policy, onToggle, onPolicyChange, saving }: ModuleRowProps) {
  const [confirmDisable, setConfirmDisable] = useState(false);

  // §2.8: no switch for a module that cannot work; say what it needs instead.
  if (blockedBy) {
    return (
      <div className="border border-[var(--rule)] bg-[var(--surface)] p-4 dark:border-[var(--rule)] dark:bg-[var(--bg-raised)]" data-testid={`module-blocked-${module.key}`}>
        <h4 className="text-sm font-semibold text-[var(--text-ter)] mb-0.5">{module.name}</h4>
        <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">{module.description}</p>
        <p className="mt-1 text-sm text-[var(--mustard)]">Needs {blockedBy}, which is off.</p>
      </div>
    );
  }

  // Not-yet-wired modules show as on with no toggle and no per-member controls.
  if (module.status === 'needs-wiring') {
    return (
      <div className="border border-[var(--rule)] bg-[var(--surface)] p-4 dark:border-[var(--rule)] dark:bg-[var(--bg-raised)]">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 mt-0.5">
            <button
              disabled
              className="relative flex-shrink-0 w-11 h-6 rounded-full bg-[var(--text-ter)] cursor-not-allowed overflow-hidden focus:outline-none"
              title="Not yet switchable."
              role="switch"
              aria-checked
            >
              <span className="block w-5 h-5 mx-0.5 my-0.5 bg-[var(--bg-raised)] rounded-full shadow translate-x-5" />
            </button>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <h4 className="text-sm font-semibold text-[var(--text-sec)] dark:text-[var(--text-pri)]">{module.name}</h4>
              <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">Not yet switchable</span>
            </div>
            <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">{module.description}</p>
            <ModuleOpenLink moduleKey={module.key} />
          </div>
        </div>
      </div>
    );
  }

  if (module.status === 'on-roadmap') {
    return (
      <div className="border border-dashed border-[var(--rule)] bg-[var(--surface)] p-4 dark:border-[var(--rule)] dark:bg-[var(--bg-raised)]">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 mt-0.5">
            <button
              disabled
              className="relative flex-shrink-0 w-11 h-6 rounded-full bg-[var(--hover-tint)] cursor-not-allowed overflow-hidden focus:outline-none"
              title="On roadmap"
            >
              <span className="block w-5 h-5 mx-0.5 my-0.5 bg-[var(--bg-raised)] rounded-full shadow" />
            </button>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <h4 className="text-sm font-semibold text-[var(--text-sec)] dark:text-[var(--text-pri)]">{module.name}</h4>
              <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">Coming soon</span>
            </div>
            <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">{module.description}</p>
            <ModuleOpenLink moduleKey={module.key} />
          </div>
        </div>
      </div>
    );
  }

  const handleToggle = () => {
    if (!enabled) {
      onToggle(module.key, true);
      return;
    }
    setConfirmDisable(true);
  };

  return (
    <div className={` border p-4 transition-colors ${
      enabled
        ? 'bg-[var(--bg-raised)] border-[var(--rule)] dark:bg-[var(--surface)] dark:border-[var(--rule)]'
        : 'bg-[var(--surface)] border-[var(--rule)] dark:bg-[var(--bg-raised)] dark:border-[var(--rule)]'
    }`}>
      <div className="flex items-start gap-4">
        {/* Toggle */}
        <div className="flex-shrink-0 mt-0.5">
          <button
            onClick={handleToggle}
            disabled={saving}
            className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 dark:focus:ring-offset-[var(--bg)] ${
              enabled ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'
            }`}
            role="switch"
            aria-checked={enabled}
            title={enabled ? `Disable ${module.name}` : `Enable ${module.name}`}
          >
            <span
              className={`block w-5 h-5 mx-0.5 my-0.5 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${
                enabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h4 className={`text-sm font-semibold ${enabled ? 'text-[var(--text-pri)] dark:text-[var(--text-pri)]' : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]'}`}>{module.name}</h4>
            {saving && (
              <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">Saving…</span>
            )}
          </div>
          <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">{module.description}</p>
          <ModuleOpenLink moduleKey={module.key} />

          {module.key === 'delegation' && enabled && getCurrentWorkspaceId() && (
            <DelegationLadderSetting workspaceId={getCurrentWorkspaceId() as string} />
          )}

          {/* Individual control (flag + override policy) */}
          <IndividualControl
            module={module}
            policy={policy}
            moduleEnabled={enabled}
            onPolicyChange={onPolicyChange}
            saving={saving}
          />
        </div>
      </div>

      {/* Disable confirmation */}
      {confirmDisable && (
        <div className="mt-3 p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)] dark:bg-[var(--mustard)]/20 dark:border-[var(--mustard)]/60">
          <div className="flex items-start gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-[var(--mustard)] flex-shrink-0 mt-0.5 dark:text-[var(--mustard)]" />
            <p className="text-sm text-[var(--mustard)] dark:text-[var(--mustard)]">{module.disableWarning}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setConfirmDisable(false);
                onToggle(module.key, false);
              }}
              className="px-3 py-1.5 text-sm bg-[var(--mustard)] text-white hover:bg-[var(--mustard)]"
            >
              Yes, disable {module.name}
            </button>
            <button
              onClick={() => setConfirmDisable(false)}
              className="px-3 py-1.5 text-sm text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)] dark:text-[var(--text-sec)] dark:bg-[var(--surface)] dark:border-[var(--rule)] dark:hover:bg-[var(--bg-raised)]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Tool Module Row — compact toggle for individual tools
// ============================================================================

interface ToolModuleRowProps {
  module: WorkspaceModule;
  enabled: boolean;
  onToggle: (key: string, enabled: boolean) => void;
  saving: boolean;
}

function ToolModuleRow({ module, enabled, onToggle, saving }: ToolModuleRowProps) {
  const [confirmDisable, setConfirmDisable] = useState(false);

  const handleToggle = () => {
    if (enabled) {
      setConfirmDisable(true);
    } else {
      onToggle(module.key, true);
    }
  };

  return (
    <div className={` border p-3 transition-colors ${
      enabled
        ? 'border-[var(--rule)] bg-[var(--bg-raised)] dark:border-[var(--rule)] dark:bg-[var(--surface)]'
        : 'border-[var(--rule)] bg-[var(--surface)] dark:border-[var(--rule)] dark:bg-[var(--bg-raised)]'
    }`}>
      <div className="flex items-start gap-3">
        <button
          onClick={handleToggle}
          disabled={saving}
          aria-checked={enabled}
          role="switch"
          className={`relative flex-shrink-0 w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary-400 mt-0.5 dark:focus:ring-offset-[var(--bg)] ${
            enabled ? 'bg-[var(--green)]' : 'bg-[var(--rule-bright)]'
          } ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <span
            className={`block absolute top-0.5 left-0.5 w-4 h-4 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${
              enabled ? 'translate-x-4' : 'translate-x-0'
            }`}
          />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium ${enabled ? 'text-[var(--text-pri)] dark:text-[var(--text-pri)]' : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]'}`}>
              {module.name}
            </span>
            {!enabled && (
              <span className="text-xs bg-[var(--hover-tint)] text-[var(--text-ter)] px-1.5 py-0.5 rounded dark:bg-[var(--surface)] dark:text-[var(--text-ter)]">
                disabled
              </span>
            )}
          </div>
          <p className={`text-xs mt-0.5 ${enabled ? 'text-[var(--text-ter)] dark:text-[var(--text-sec)]' : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]'}`}>
            {module.description}
          </p>
        </div>
      </div>

      {confirmDisable && (
        <div className="mt-3 pt-3 border-t border-[var(--mustard)] dark:border-[var(--mustard)]/40 space-y-2">
          <div className="flex items-start gap-2 p-2 bg-[var(--mustard-dim)] border border-[var(--mustard)] dark:bg-[var(--mustard)]/20 dark:border-[var(--mustard)]/50">
            <AlertTriangle className="h-3.5 w-3.5 text-[var(--mustard)] flex-shrink-0 mt-0.5 dark:text-[var(--mustard)]" />
            <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">{module.disableWarning}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { setConfirmDisable(false); onToggle(module.key, false); }}
              className="px-2.5 py-1 text-xs bg-[var(--mustard)] text-white rounded hover:bg-[var(--mustard)]"
            >
              Disable
            </button>
            <button
              onClick={() => setConfirmDisable(false)}
              className="px-2.5 py-1 text-xs text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] rounded hover:bg-[var(--surface)] dark:text-[var(--text-sec)] dark:bg-[var(--surface)] dark:border-[var(--rule)] dark:hover:bg-[var(--bg-raised)]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Page
// ============================================================================

export default function Modules() {
  const workspaceId = getCurrentWorkspaceId();
  // §2.8: the switches are drawn only for someone who may use them. The
  // set_workspace_module RPC stays the authority (admin-checked server-side).
  const canManage = useUserStore((s) => s.can('admin.settings'));
  const elevationTeam = useIsElevationTeam();
  const addNotification = useUIStore((s) => s.addNotification);
  const [features, setFeatures] = useState<Record<string, boolean>>({});
  const [policies, setPolicies] = useState<Record<string, ModulePolicy>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getWorkspaceModules(workspaceId).then(({ data, policies: pol, error: err }) => {
      if (err) setError(err);
      else {
        setFeatures(data);
        setPolicies(pol);
      }
      setLoading(false);
    });
  }, [workspaceId]);

  const handleToggle = async (moduleKey: string, enabled: boolean) => {
    if (!workspaceId) return;
    setSaving(moduleKey);

    setFeatures((prev) => ({ ...prev, [moduleKey]: enabled }));

    const { error: saveErr } = await setWorkspaceModule(workspaceId, moduleKey, enabled);
    setSaving(null);

    if (saveErr) {
      setFeatures((prev) => ({ ...prev, [moduleKey]: !enabled }));
      setError(saveErr);
      addNotification({ type: 'error', title: 'Save failed', message: saveErr, duration: 5000 });
      return;
    }

    const mod = WORKSPACE_MODULES.find((m) => m.key === moduleKey);
    addNotification({
      type: 'success',
      title: `${mod?.name ?? moduleKey} ${enabled ? 'enabled' : 'disabled'}`,
      message: enabled
        ? `The ${mod?.name} module is now active for this workspace.`
        : `The ${mod?.name} module has been disabled. Members will no longer see it.`,
      duration: 5000,
    });
  };

  const handlePolicyChange = async (moduleKey: string, partial: Partial<ModulePolicy>) => {
    if (!workspaceId) return;
    setSaving(moduleKey);

    setPolicies((prev) => ({
      ...prev,
      [moduleKey]: { ...(prev[moduleKey] ?? { override: 'all' }), ...partial },
    }));

    const { error: saveErr } = await setWorkspaceModulePolicy(workspaceId, moduleKey, partial);
    setSaving(null);

    if (saveErr) {
      setError(saveErr);
      addNotification({ type: 'error', title: 'Policy save failed', message: saveErr, duration: 5000 });
      getWorkspaceModules(workspaceId).then(({ policies: pol }) => setPolicies(pol));
      return;
    }

    const mod = WORKSPACE_MODULES.find((m) => m.key === moduleKey);
    const label =
      partial.override === 'none' && partial.userDefault === true
        ? 'Always On (no override · default on)'
        : partial.override
          ? `override: ${OVERRIDE_LABELS[partial.override]}`
          : `default: ${partial.userDefault ? 'on' : 'off'}`;
    addNotification({
      type: 'success',
      title: `${mod?.name ?? moduleKey} policy updated`,
      message: `Individual control updated — ${label}.`,
      duration: 4000,
    });
  };

  const getPolicy = (moduleKey: string): ModulePolicy =>
    getModulePolicy(policies, moduleKey);

  // A team-level module (Team pages) is listed for the Elevation team only,
  // and for nobody until it is known who is signed in (lib/elevationAccess.ts).
  const listed = WORKSPACE_MODULES.filter(
    (m) => !m.hideFromModulesSettings && isModuleListedFor(m, elevationTeam),
  );
  const platformModules = listed.filter((m) => getModulePageSection(m) === 'platform');
  const previewModules = listed.filter((m) => getModulePageSection(m) === 'previews');
  const toolModules = listed.filter(
    (m) =>
      getModulePageSection(m) === 'tools' &&
      !isSwitchedOffModuleKey(m.key),
  );

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<ToggleLeft className="h-4 w-4 text-[var(--text-sec)]" />}
        iconBg="bg-[var(--hover-tint)]"
        title="Platform Modules"
        subtitle="Every module starts on. Turn one off here to hide it from everyone in this workspace, and choose who can override it and what members get by default."
      />

      {workspaceId && !canManage && (
        <div className="card">
          <p className="text-sm text-[var(--text-sec)]">
            Only a workspace admin can turn modules on or off. Ask a workspace admin if one should change.
          </p>
        </div>
      )}

      {!workspaceId && (
        <div className="card bg-[var(--mustard-dim)] border-[var(--mustard)] dark:bg-[var(--mustard)]/20 dark:border-[var(--mustard)]/60">
          <p className="text-sm text-[var(--mustard)] dark:text-[var(--mustard)]">Select a workspace in the sidebar to manage its modules.</p>
        </div>
      )}

      {error && (
        <div className="card bg-[var(--coral-dim)] border-[var(--coral)] dark:bg-[var(--coral)]/20 dark:border-[var(--coral)]/60">
          <p className="text-sm text-[var(--coral)] dark:text-[var(--coral)]">{error}</p>
        </div>
      )}

      {workspaceId && canManage && (
        <>
          {loading ? (
            <div className="card">
              <div className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] py-4 text-center">Loading module settings…</div>
            </div>
          ) : (
            <div className="space-y-10">
              <ModulesPageSection
                sectionId="platform"
                modules={platformModules}
                features={features}
                getPolicy={getPolicy}
                onToggle={handleToggle}
                onPolicyChange={handlePolicyChange}
                saving={saving}
              />
              <ModulesPageSection
                sectionId="previews"
                modules={previewModules}
                features={features}
                getPolicy={getPolicy}
                onToggle={handleToggle}
                onPolicyChange={handlePolicyChange}
                saving={saving}
              />
              <ModulesPageSection
                sectionId="tools"
                modules={toolModules}
                features={features}
                getPolicy={getPolicy}
                onToggle={handleToggle}
                onPolicyChange={handlePolicyChange}
                saving={saving}
              />
            </div>
          )}
        </>
      )}

      <div className="card bg-[var(--slate-dim)] border-[var(--slate)] space-y-2 dark:bg-[var(--slate)]/20 dark:border-[var(--slate)]/60">
        <p className="text-sm font-medium text-[var(--text-sec)]">How modules and feature flags work together</p>
        <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-ter)]/90">
          <strong>Module toggle</strong> — workspace-level switch. Controls whether a capability is
          available to anyone in the workspace at all.
        </p>
        <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-ter)]/90">
          <strong>Individual control</strong> — when a module is on, members with override permission
          can further toggle their personal flag in Settings → Feature Flags. The module's
          "default for members" setting determines whether they start with it on or off.
        </p>
        <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-ter)]/90">
          Both must be enabled for a member to see a feature. Module off → nobody sees it,
          regardless of their personal flag.
        </p>
      </div>
    </div>
  );
}
